import { Injectable } from '@angular/core';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';

export interface ScheduleFieldRow {
  label: string;
  values: string[];
  hasDiff: boolean;
}

export interface SequenceGroup {
  label: string;
  fields: ScheduleFieldRow[];
}

@Injectable({ providedIn: 'root' })
export class ScheduleFormatService {
  formatDate(value: string | undefined): string {
    if (!value) return '—';
    return value.slice(0, 10);
  }

  formatDurationSeconds(seconds: number | undefined): string {
    if (seconds === undefined || seconds === null) return '—';
    const totalMinutes = Math.floor(seconds / 60);
    const d = Math.floor(totalMinutes / 1440);
    const h = Math.floor((totalMinutes % 1440) / 60);
    const m = totalMinutes % 60;
    const parts: string[] = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (m || parts.length === 0) parts.push(`${m}m`);
    return parts.join(' ');
  }

  formatDurationMinutes(minutes: number | undefined): string {
    if (minutes === undefined || minutes === null) return '—';
    const d = Math.floor(minutes / 1440);
    const h = Math.floor((minutes % 1440) / 60);
    const m = minutes % 60;
    const parts: string[] = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (m || parts.length === 0) parts.push(`${m}m`);
    return parts.join(' ');
  }

  formatDurationHms(seconds: number | undefined): string {
    if (seconds === undefined || seconds === null) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const parts: string[] = [];
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    if (s || parts.length === 0) parts.push(`${s}s`);
    return parts.join(' ');
  }

  formatValue(value: unknown): string {
    if (value === undefined || value === null) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  makeDiffRow(label: string, values: string[]): ScheduleFieldRow {
    const hasDiff = values.length > 1 && !values.every((v) => v === values[0]);
    return { label, values, hasDiff };
  }

  buildRuleFields(collarRules: (dsDtos.ScheduleRule | undefined)[]): ScheduleFieldRow[] {
    return [
      this.makeDiffRow(
        'Start Date',
        collarRules.map((r) => this.formatDate(r?.startTime)),
      ),
      this.makeDiffRow(
        'End Date',
        collarRules.map((r) => this.formatDate(r?.endTime)),
      ),
      this.makeDiffRow(
        'Period Length',
        collarRules.map((r) => this.formatDurationMinutes(r?.periodLength)),
      ),
      this.makeDiffRow(
        'Skip Count',
        collarRules.map((r) => this.formatValue(r?.skipCount)),
      ),
    ];
  }

  buildSequenceGroups(
    collarRules: (dsDtos.ScheduleRule | undefined)[],
    collarCount: number,
    isCommunication = false,
  ): SequenceGroup[] {
    const maxSeqCount = Math.max(...collarRules.map((r) => r?.sequences?.length ?? 0), 0);
    const groups: SequenceGroup[] = [];
    for (let s = 0; s < maxSeqCount; s++) {
      const seqRow = (field: (seq: dsDtos.ScheduleSequence | undefined) => string) =>
        Array.from({ length: collarCount }, (_, ci) => field(collarRules[ci]?.sequences?.[s]));
      groups.push({
        label: `Sequence ${s + 1}`,
        fields: [
          this.makeDiffRow(
            isCommunication ? 'Communication Interval' : 'Fix Rate',
            seqRow((seq) => this.formatDurationSeconds(seq?.fixRate)),
          ),
          this.makeDiffRow(
            'Duration',
            seqRow((seq) => this.formatDurationMinutes(seq?.duration)),
          ),
          this.makeDiffRow(
            'Offset',
            seqRow((seq) => this.formatDurationMinutes(seq?.offset)),
          ),
        ],
      });
    }
    return groups;
  }

  buildCameraRuleFields(
    collarRules: (dsDtos.CameraScheduleRule | undefined)[],
  ): ScheduleFieldRow[] {
    return [
      this.makeDiffRow(
        'Type',
        collarRules.map((r) => this.formatValue(r?.cameraScheduleRuleType)),
      ),
      this.makeDiffRow(
        'Main Trigger',
        collarRules.map((r) => this.formatValue(r?.cameraScheduleRuleMainTrigger)),
      ),
      this.makeDiffRow(
        'Trigger Value',
        collarRules.map((r) =>
          this.formatMainTriggerValue(r?.mainTriggerValue, r?.cameraScheduleRuleMainTrigger),
        ),
      ),
      this.makeDiffRow(
        'Start Date',
        collarRules.map((r) => this.formatDate(r?.startTime)),
      ),
      this.makeDiffRow(
        'End Date',
        collarRules.map((r) => this.formatDate(r?.endTime)),
      ),
      this.makeDiffRow(
        'Cycle Time',
        collarRules.map((r) => this.formatDurationSeconds(r?.cycleTime)),
      ),
      this.makeDiffRow(
        'On Time',
        collarRules.map((r) => this.formatDurationSeconds(r?.onTime)),
      ),
      this.makeDiffRow(
        'Size',
        collarRules.map((r) => this.formatSize(r?.size, r?.cameraScheduleRuleType)),
      ),
      this.makeDiffRow(
        'Picture Interval',
        collarRules.map((r) => this.formatValue(r?.pictureInterval)),
      ),
      this.makeDiffRow(
        'Inside VF Only',
        collarRules.map((r) => this.formatValue(r?.ruleOnlyValidInsideVirtualFence)),
      ),
      this.makeDiffRow(
        'Outside VF Only',
        collarRules.map((r) => this.formatValue(r?.ruleOnlyValidOutsideVirtualFence)),
      ),
    ];
  }

  formatMainTriggerValue(
    value: number | undefined,
    trigger: dsEnums.CameraScheduleRuleMainTrigger | undefined,
  ): string {
    if (value === undefined || value === null) return '—';
    switch (trigger) {
      case 'GPS':
        return `${value} fix(es)`;
      case 'TIMER':
      case 'PROXIMITY_PRELIMANARY':
        return this.formatDurationMinutes(value);
      default:
        return this.formatValue(value);
    }
  }

  formatSize(value: number | undefined, type: dsEnums.CameraScheduleRuleType | undefined): string {
    if (value === undefined || value === null) return '—';
    switch (type) {
      case 'PICTURE':
        return `${value} photo(s)`;
      case 'VIDEO':
        return this.formatDurationHms(value);
      default:
        return this.formatValue(value);
    }
  }
}
